import { useCallback, useEffect, useMemo, useState } from 'react';
import { reportApi } from '../api/api';

function toInputDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultDateRangeStrings() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return { startDate: toInputDateString(start), endDate: toInputDateString(end) };
}

/** Inclusive local day bounds for YYYY-MM-DD strings */
function parseRangeBounds(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return null;
  const [sy, sm, sd] = startDateStr.split('-').map(Number);
  const [ey, em, ed] = endDateStr.split('-').map(Number);
  if (
    [sy, sm, sd, ey, em, ed].some(
      (n) => Number.isNaN(n) || n === undefined
    )
  ) {
    return null;
  }
  const startMs = new Date(sy, sm - 1, sd, 0, 0, 0, 0).getTime();
  const endMs = new Date(ey, em - 1, ed, 23, 59, 59, 999).getTime();
  if (startMs > endMs) return null;
  return { startMs, endMs };
}

function formatRangeLabel(startStr, endStr) {
  if (!startStr || !endStr) return '';
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  if ([sy, sm, sd, ey, em, ed].some((n) => Number.isNaN(n))) return '';
  const a = new Date(sy, sm - 1, sd);
  const b = new Date(ey, em - 1, ed);
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  const left = a.toLocaleDateString(undefined, opts);
  const right = b.toLocaleDateString(undefined, opts);
  return left === right ? left : `${left} → ${right}`;
}

function formatTzMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function rangeDayCount(startStr, endStr) {
  const b = parseRangeBounds(startStr, endStr);
  if (!b) return 0;
  return Math.round((b.endMs - b.startMs) / 86400000) + 1;
}

function isProfitLossShape(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return (
    obj.summary != null ||
    obj.sales != null ||
    obj.buying != null ||
    obj.expenses != null ||
    obj.products != null
  );
}

/** Unwrap GET body whether API returns the payload at root, under returnData, or under data. */
function normalizeProfitAndLossPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (isProfitLossShape(raw)) return raw;
  if (raw.returnData && typeof raw.returnData === 'object') {
    const inner = raw.returnData;
    if (isProfitLossShape(inner)) return inner;
  }
  if (raw.data && typeof raw.data === 'object') {
    const inner = raw.data;
    if (isProfitLossShape(inner)) return inner;
  }
  return null;
}

function pickProductLabel(row) {
  if (!row || typeof row !== 'object') return '—';
  const p = row.product && typeof row.product === 'object' ? row.product : null;
  const v =
    row.product_name ??
    p?.name ??
    row.name ??
    row.description ??
    row.title ??
    row.item_name;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '—';
}

function lineQty(row) {
  const q = Number(row.quantity ?? row.qty ?? row.count ?? 1);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

function lineSellingTotal(row) {
  const qty = lineQty(row);
  const direct = Number(
    row.selling_total ??
      row.total_selling ??
      row.sales_amount ??
      row.amount ??
      row.total ??
      row.line_total ??
      row.subtotal ??
      row.line_amount ??
      NaN
  );
  if (Number.isFinite(direct)) return direct;

  const unit = Number(
    row.unit_selling_price ??
      row.selling_price ??
      row.sale_price ??
      row.unit_price ??
      row.price ??
      row.rate ??
      NaN
  );
  if (Number.isFinite(unit)) return unit * qty;
  return 0;
}

function lineBuyingTotal(row) {
  const qty = lineQty(row);
  const p = row.product && typeof row.product === 'object' ? row.product : null;
  const direct = Number(
    row.buying_total ??
      row.total_buying ??
      row.cost_total ??
      row.purchase_total ??
      row.cost ??
      NaN
  );
  if (Number.isFinite(direct) && direct >= 0) return direct;

  const unit = Number(
    row.unit_buying_price ??
      row.buying_unit_price ??
      p?.buying_price ??
      row.buying_price ??
      row.cost_price ??
      row.purchase_price ??
      NaN
  );
  if (Number.isFinite(unit)) return unit * qty;
  return 0;
}

function lineProfit(row, selling, buying) {
  const explicit = Number(
    row.profit ?? row.line_profit ?? row.gross_profit ?? row.margin ?? NaN
  );
  if (Number.isFinite(explicit)) return explicit;
  return selling - buying;
}

/**
 * One row per aggregate key (product id when present); sums lines for the same product.
 */
function buildProductProfitRows(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const map = new Map();
  items.forEach((row, idx) => {
    const name = pickProductLabel(row);
    const key =
      row.product_id != null && row.product_id !== ''
        ? `pid:${row.product_id}`
        : row.product?.id != null && row.product.id !== ''
          ? `pid:${row.product.id}`
          : row.uuid != null
            ? `uuid:${row.uuid}`
            : `i:${idx}`;
    const selling = lineSellingTotal(row);
    const buying = lineBuyingTotal(row);
    const profit = lineProfit(row, selling, buying);
    const prev = map.get(key);
    if (prev) {
      prev.selling += selling;
      prev.buying += buying;
      prev.profit += profit;
    } else {
      map.set(key, { key, name, selling, buying, profit });
    }
  });
  return Array.from(map.values()).sort((a, b) => {
    const dp = b.profit - a.profit;
    if (dp !== 0) return dp;
    return b.name.localeCompare(a.name, undefined, { sensitivity: 'base' });
  });
}

const PRODUCT_ROWS_PREVIEW = 3;

export function Reports() {
  const defaults = useMemo(() => defaultDateRangeStrings(), []);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);

  const [plLoading, setPlLoading] = useState(false);
  const [plError, setPlError] = useState(null);
  const [plData, setPlData] = useState(null);
  const [productListExpanded, setProductListExpanded] = useState(false);

  const fetchProfitAndLoss = useCallback(async () => {
    const bounds = parseRangeBounds(startDate, endDate);
    if (!bounds) {
      setPlData(null);
      setPlError(null);
      return;
    }
    setPlLoading(true);
    setPlError(null);
    try {
      const resp = await reportApi.profitAndLoss({ startDate, endDate });
      const normalized = normalizeProfitAndLossPayload(resp?.data);
      if (!normalized) {
        setPlData(null);
        setPlError('Unexpected response from profit and loss report.');
        return;
      }
      setPlData(normalized);
    } catch (err) {
      setPlData(null);
      setPlError(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          err?.message ||
          'Could not load profit and loss report.'
      );
    } finally {
      setPlLoading(false);
    }
  }, [startDate, endDate]);

  const rangeInvalid = Boolean(
    startDate &&
      endDate &&
      parseRangeBounds(startDate, endDate) === null
  );

  useEffect(() => {
    if (rangeInvalid) {
      setPlData(null);
      setPlError(null);
      return;
    }
    fetchProfitAndLoss();
  }, [startDate, endDate, fetchProfitAndLoss, rangeInvalid]);

  const rangeLabel = useMemo(
    () => formatRangeLabel(startDate, endDate),
    [startDate, endDate]
  );

  const rangeDays = useMemo(
    () => rangeDayCount(startDate, endDate),
    [startDate, endDate]
  );

  const productSourceItems = useMemo(() => {
    if (!plData) return [];
    if (Array.isArray(plData.products) && plData.products.length > 0) {
      return plData.products;
    }
    const salesItems = Array.isArray(plData.sales?.items)
      ? plData.sales.items
      : [];
    if (salesItems.length > 0) return salesItems;
    return Array.isArray(plData.buying?.items) ? plData.buying.items : [];
  }, [plData]);

  const productProfitRows = useMemo(
    () => buildProductProfitRows(productSourceItems),
    [productSourceItems]
  );

  const productRowsVisible = useMemo(
    () =>
      productListExpanded
        ? productProfitRows
        : productProfitRows.slice(0, PRODUCT_ROWS_PREVIEW),
    [productProfitRows, productListExpanded]
  );

  useEffect(() => {
    setProductListExpanded(false);
  }, [plData, startDate, endDate]);

  const summaryMetrics = useMemo(() => {
    if (!plData) {
      return {
        totalSale: 0,
        totalBuyingPrice: 0,
        totalExpense: 0,
        netProfit: 0,
      };
    }
    const s = plData.summary ?? {};
    const totalSale = Number(
      s.total_sales ?? plData.sales?.total ?? 0
    );
    const totalBuyingPrice = Number(
      s.total_buying_price ??
        plData.buying?.total_buying_price ??
        plData.buying?.total ??
        0
    );
    const totalExpense = Number(
      s.total_expenses ?? plData.expenses?.total ?? 0
    );
    let netProfit = Number(s.net_profit);
    if (!Number.isFinite(netProfit)) {
      netProfit =
        (Number.isFinite(totalSale) ? totalSale : 0) -
        (Number.isFinite(totalBuyingPrice) ? totalBuyingPrice : 0) -
        (Number.isFinite(totalExpense) ? totalExpense : 0);
    }
    return {
      totalSale: Number.isFinite(totalSale) ? totalSale : 0,
      totalBuyingPrice: Number.isFinite(totalBuyingPrice) ? totalBuyingPrice : 0,
      totalExpense: Number.isFinite(totalExpense) ? totalExpense : 0,
      netProfit: Number.isFinite(netProfit) ? netProfit : 0,
    };
  }, [plData]);

  return (
    <div className="page page-reports">
      <header className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">
          Profit and loss: totals from the server summary, plus per-product selling,
          buying, and profit for the selected range.
        </p>
      </header>

      <section className="card reports-filter-card" aria-label="Report date range">
        <div className="reports-filter-head">
          <div>
            <h2 className="reports-filter-title">Date range</h2>
            {rangeLabel && !rangeInvalid && (
              <p className="reports-range-summary">
                {rangeLabel}
                {rangeDays > 0 ? ` · ${rangeDays} day${rangeDays === 1 ? '' : 's'}` : ''}
              </p>
            )}
          </div>
        </div>
        <div className="reports-date-row">
          <label className="field field--inline reports-date-field">
            <span className="field-label">From</span>
            <input
              className="input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || undefined}
            />
          </label>
          <label className="field field--inline reports-date-field">
            <span className="field-label">To</span>
            <input
              className="input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
          </label>
        </div>
        {rangeInvalid && (
          <p className="reports-date-error" role="alert">
            End date must be on or after start date.
          </p>
        )}
      </section>

      <section className="card reports-card reports-card--wide reports-pl-card">
        <div className="card-toolbar">
          <h2 className="card-title">Profit &amp; loss</h2>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => fetchProfitAndLoss()}
            disabled={rangeInvalid || plLoading}
          >
            {plLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {rangeInvalid ? (
          <p className="empty-hint reports-empty-inline">
            Fix the date range to load profit and loss.
          </p>
        ) : plLoading && !plData ? (
          <p className="empty-hint reports-empty-inline">Loading report…</p>
        ) : plError ? (
          <p className="modal-error" role="alert">
            {plError}
          </p>
        ) : plData ? (
          <>
            {(plData.period?.from != null || plData.period?.to != null) && (
              <p className="reports-pl-period mono">
                Period: {String(plData.period?.from ?? '')} →{' '}
                {String(plData.period?.to ?? '')}
              </p>
            )}

            <div className="stat-row stat-row--reports stat-row--reports-pl-four">
              <div
                className={`stat-card ${
                  summaryMetrics.netProfit < 0
                    ? 'stat-card--warn'
                    : 'stat-card--ok'
                }`}
              >
                <span className="stat-label">Profit (TZS)</span>
                <span className="stat-value">
                  {formatTzMoney(summaryMetrics.netProfit)}
                </span>
              </div>
              <div className="stat-card stat-card--accent">
                <span className="stat-label">Total sale (TZS)</span>
                <span className="stat-value">
                  {formatTzMoney(summaryMetrics.totalSale)}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Total buying price (TZS)</span>
                <span className="stat-value">
                  {formatTzMoney(summaryMetrics.totalBuyingPrice)}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Total expense (TZS)</span>
                <span className="stat-value">
                  {formatTzMoney(summaryMetrics.totalExpense)}
                </span>
              </div>
            </div>

            <div className="reports-pl-product-block">
              <div className="reports-pl-product-head">
                <div>
                  <h3 className="reports-pl-product-title">By product</h3>
                  <p className="reports-pl-product-hint">
                    Sorted by profit (highest first). Selling, buying, and profit
                    per product; same product ids are combined.
                  </p>
                </div>
                {productProfitRows.length > PRODUCT_ROWS_PREVIEW && (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost reports-pl-product-toggle"
                    onClick={() => setProductListExpanded((v) => !v)}
                    aria-expanded={productListExpanded}
                  >
                    {productListExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
              <div className="table-wrap">
                <table className="table reports-pl-product-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Selling price (TZS)</th>
                      <th>Buying price (TZS)</th>
                      <th>Profit (TZS)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productProfitRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="empty-cell">
                          No product lines in this period. When the API adds items
                          under <span className="mono">sales.items</span>,{' '}
                          <span className="mono">buying.items</span>, or{' '}
                          <span className="mono">products</span>, they will appear
                          here.
                        </td>
                      </tr>
                    ) : (
                      productRowsVisible.map((r) => (
                        <tr key={r.key}>
                          <td>{r.name}</td>
                          <td className="mono">{formatTzMoney(r.selling)}</td>
                          <td className="mono">{formatTzMoney(r.buying)}</td>
                          <td
                            className={`mono ${
                              r.profit < 0 ? 'reports-pl-profit--neg' : ''
                            }`}
                          >
                            {formatTzMoney(r.profit)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}
